import { Component, ElementRef, OnInit, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../environments/environment';
import { AGENT_WELCOME, DEFAULT_AGENT_WELCOME } from '../../config/agent-welcome';
import { MsalService } from '@azure/msal-angular';
import { getMsalScopesForActiveClientId, getSilentRedirectUri } from '../../utils/msal-config';
import {
  ChatRichBlock,
  ChatRichResponseComponent,
  ChatRichSource,
  ChatRichTable
} from '../shared/chat-rich-response/chat-rich-response.component';

interface UiMessage {
  role: 'user' | 'assistant';
  content: string;
  at: string;
  durationMs?: number;
  usage?: FoundryUsageResponse;
  summary?: string;
  tables?: ChatRichTable[];
  blocks?: ChatRichBlock[];
  sources?: ChatRichSource[];
  /** Archivos que acompañaron a este mensaje, para que queden visibles en el historial. */
  attachments?: { name: string; sizeBytes: number }[];
}

interface ReferenceFoundryChatResponse {
  Url: string;
  SasUrl: string;
  ContentType: string;
  FileName: string;
  Folder: string;
  PageNumber: number;
  ImageUrl?: string;
  ImageBase64?: string;
}

interface FoundryChatResponse {
  ConversationId: string;
  Reply: string;
  References?: ReferenceFoundryChatResponse[];
  Usage?: FoundryUsageResponse;
  TokenLimits?: FoundryTokenLimitResponse;
  CreatedUtc: string;
}

interface FoundryUsageResponse {
  InputTokens: number;
  OutputTokens: number;
  ReasoningTokens: number;
  TotalTokens: number;
}

interface ConversationUsageTotals {
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
}

interface FoundryTokenLimitResponse {
  InputTokensEnabled: number;
  OutputTokensEnabled: number;
  InputTokensUsed: number;
  OutputTokensUsed: number;
  InputTokensRemaining: number;
  OutputTokensRemaining: number;
  InputTokensPercentUsed: number;
  OutputTokensPercentUsed: number;
  ShouldWarn: boolean;
  IsBlocked: boolean;
  Message: string;
}

interface FoundryClientSettings {
  ShowUsageDebugMetrics: boolean;
  UsageDashboardEnabled: boolean;
  ConversationInactivityTimeoutMinutes: number;
}

interface UsageDashboardSummary {
  Domain: string;
  Sessions: number;
  Messages: number;
  ActiveUsers: number;
  FailedMessages: number;
  InputTokens: number;
  OutputTokens: number;
  InputTokensEnabled: number;
  OutputTokensEnabled: number;
  InputTokensRemaining: number;
  OutputTokensRemaining: number;
  InputTokensPercentUsed: number;
  OutputTokensPercentUsed: number;
  ReasoningTokens: number;
  TotalTokens: number;
  AverageDurationMs: number;
  UpdatedUtc: string;
}

interface FoundryHealthResponse {
  AgentId: string;
  Available: boolean;
  Detail: string;
  StatusCode?: number;
  CheckedUtc: string;
}

const DEFAULT_FOUNDRY_CLIENT_SETTINGS: FoundryClientSettings = {
  ShowUsageDebugMetrics: false,
  UsageDashboardEnabled: false,
  ConversationInactivityTimeoutMinutes: 10
};

/** Ficha de un archivo ya preparado en el backend, a la espera de enviarse con el mensaje. */
interface AttachmentChip {
  id: string;
  name: string;
  kind: string;
  sizeBytes: number;
}

@Component({
  selector: 'app-custom-foundry-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatRichResponseComponent],
  template: `
    <div class="h-full w-full flex flex-col bg-white dark:bg-black">
      <div class="px-6 py-3 border-b border-gray-100 dark:border-white/5 flex items-center justify-between flex-shrink-0">
        <div>
          <h3 class="font-semibold text-sm text-gray-800 dark:text-gray-200">{{ title() }}</h3>
          <div class="flex items-center gap-2 mt-1">
            <span
              class="w-2 h-2 rounded-full"
              [ngClass]="{
                'bg-emerald-400 animate-pulse': healthStatus() === 'checking',
                'bg-emerald-500': healthStatus() === 'online',
                'bg-red-500': healthStatus() === 'offline'
              }"
            ></span>
            <p class="text-[10px] text-gray-400 leading-none">
              @switch (healthStatus()) {
                @case ('online') {
                  En línea
                }
                @case ('offline') {
                  No disponible
                }
                @default {
                  Verificando disponibilidad...
                }
              }
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          @if (showUsageDebugMetrics()) {
            <div class="hidden sm:flex items-center gap-1.5 text-[10px] leading-tight text-gray-400 dark:text-gray-500" title="Tokens consumidos en esta conversación">
              
              <div class="flex flex-col items-end">
              <span class="flex items-center gap-1" title="Tokens de entrada: lo que tú envías al agente">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-emerald-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19V5m0 0-6 6m6-6 6 6" /></svg>
                <span class="tabular-nums">{{ formatTokenCount(conversationUsageTotals().inputTokens) }}</span>
                <span class="sr-only">tokens de entrada</span>
              </span>
              <span class="flex items-center gap-1" title="Tokens de salida: lo que el agente responde">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-sky-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m0 0 6-6m-6 6-6-6" /></svg>
                <span class="tabular-nums">{{ formatTokenCount(conversationUsageTotals().outputTokens) }}</span>
                <span class="sr-only">tokens de salida</span>
              </span>
              </div>
            </div>
          }
          @if (showUsageDashboardButton()) {
            <button
              (click)="openUsageDashboard()"
              title="Ver el consumo de tokens de este agente"
              aria-label="Ver el consumo de tokens de este agente"
              class="text-[11px] px-2.5 py-1.5 rounded text-[rgb(20_182_131)] hover:bg-[rgb(20_182_131/0.1)] hover:border-[rgb(20_182_131)] dark:border-[rgb(20_182_131/0.4)] dark:text-[rgb(20_182_131)] dark:hover:bg-[rgb(20_182_131/0.15)] dark:hover:border-[rgb(20_182_131)] transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[rgb(20_182_131/0.3)] transition-colors"
            >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"> <defs> <!-- Gradiente cian a púrpura para el texto y acentos --> <linearGradient id="tokenGradient" x1="0%" y1="0%" x2="100%" y2="100%"> <stop offset="0%" stop-color="#22d3ee" /> <stop offset="100%" stop-color="#c084fc" /> </linearGradient> </defs> <!-- Anillos concéntricos estilo token/cripto --> <circle cx="12" cy="12" r="10" fill="none" stroke="url(#tokenGradient)" stroke-width="1.2" opacity="0.8" /> <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3" stroke-dasharray="2 1.5" /> <!-- Nodos de conexión (superior e inferior) --> <circle cx="12" cy="2" r="1" fill="url(#tokenGradient)" /> <circle cx="12" cy="22" r="1" fill="url(#tokenGradient)" /> <circle cx="2" cy="12" r="0.8" fill="currentColor" opacity="0.5" /> <circle cx="22" cy="12" r="0.8" fill="currentColor" opacity="0.5" /> <!-- Identificador M31 --> <text x="12" y="15.2" font-family="'Inter', 'Segoe UI', system-ui, sans-serif" font-weight="900" font-size="8.5" text-anchor="middle" fill="url(#tokenGradient)" letter-spacing="-0.5">M31</text> </svg>
            </button>
          }
          <button
            (click)="exportConversationPdf()"
            [disabled]="messages().length === 0"
            title="Descargar esta conversación en PDF"
            aria-label="Descargar esta conversación en PDF"
            class="group inline-flex items-center gap-1 px-1.5 py-1 text-[11px] px-2.5 py-1.5 rounded text-[rgb(20_182_131)] hover:bg-[rgb(20_182_131/0.1)] hover:border-[rgb(20_182_131)] dark:border-[rgb(20_182_131/0.4)] dark:text-[rgb(20_182_131)] dark:hover:bg-[rgb(20_182_131/0.15)] dark:hover:border-[rgb(20_182_131)] transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[rgb(20_182_131/0.3)] transition-colors"
          >
            <svg viewBox="0 0 24 24" class="h-5 w-5" aria-hidden="true">
              <path fill="#D64B45" d="M14 2H6.5A1.5 1.5 0 0 0 5 3.5v17A1.5 1.5 0 0 0 6.5 22h11a1.5 1.5 0 0 0 1.5-1.5V7l-5-5Z" />
              <path fill="#A3231D" d="M14 2v3.5A1.5 1.5 0 0 0 15.5 7H19l-5-5Z" />
              <text
                x="12"
                y="18.6"
                text-anchor="middle"
                font-size="6.4"
                font-weight="700"
                letter-spacing="-0.2"
                fill="#fff"
              >PDF</text>
            </svg>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="h-3 w-3 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v11m0 0 4-4m-4 4-4-4M4 19h16" />
            </svg>
          </button>
          <button
            (click)="resetConversation()"
            title="Empezar una conversación nueva (se borra la actual)"
            aria-label="Empezar una conversación nueva"
            class="inline-flex items-center justify-center gap-2 text-xs font-medium px-3 py-1.5 rounded-lg border border-[rgb(20_182_131/0.3)] text-[rgb(20_182_131)] hover:bg-[rgb(20_182_131/0.1)] hover:border-[rgb(20_182_131)] dark:border-[rgb(20_182_131/0.4)] dark:text-[rgb(20_182_131)] dark:hover:bg-[rgb(20_182_131/0.15)] dark:hover:border-[rgb(20_182_131)] transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[rgb(20_182_131/0.3)]"
          >
            +
          </button>
        </div>
      </div>

      @if (usageDashboardVisible()) {
        <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4">
          <div class="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-zinc-950">
            <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-white/10">
              <div>
                <div class="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"> <defs> <!-- Gradiente cian a púrpura para el texto y acentos --> <linearGradient id="tokenGradient" x1="0%" y1="0%" x2="100%" y2="100%"> <stop offset="0%" stop-color="#22d3ee" /> <stop offset="100%" stop-color="#c084fc" /> </linearGradient> </defs> <!-- Anillos concéntricos estilo token/cripto --> <circle cx="12" cy="12" r="10" fill="none" stroke="url(#tokenGradient)" stroke-width="1.2" opacity="0.8" /> <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3" stroke-dasharray="2 1.5" /> <!-- Nodos de conexión (superior e inferior) --> <circle cx="12" cy="2" r="1" fill="url(#tokenGradient)" /> <circle cx="12" cy="22" r="1" fill="url(#tokenGradient)" /> <circle cx="2" cy="12" r="0.8" fill="currentColor" opacity="0.5" /> <circle cx="22" cy="12" r="0.8" fill="currentColor" opacity="0.5" /> <!-- Identificador M31 --> <text x="12" y="15.2" font-family="'Inter', 'Segoe UI', system-ui, sans-serif" font-weight="900" font-size="8.5" text-anchor="middle" fill="url(#tokenGradient)" letter-spacing="-0.5">M31</text> </svg>
                  <span>Tokens de la organización</span>
                </div>
                <div class="text-[11px] text-gray-400 dark:text-gray-500">{{ usageDashboard()?.Domain || 'cargando...' }}</div>
              </div>
              <button
                (click)="usageDashboardVisible.set(false)"
                class="rounded border border-gray-200 px-2 py-1 text-xs  border border-[rgb(20_182_131/0.3)] text-[rgb(20_182_131)] hover:bg-[rgb(20_182_131/0.1)] hover:border-[rgb(20_182_131)] dark:border-[rgb(20_182_131/0.4)] dark:text-[rgb(20_182_131)] dark:hover:bg-[rgb(20_182_131/0.15)] dark:hover:border-[rgb(20_182_131)] transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[rgb(20_182_131/0.3)]"
              >
                X
              </button>
            </div>
            <div class="p-4">
              @if (usageDashboardLoading()) {
                <div class="flex flex-col items-center justify-center py-10" role="status" aria-live="polite">
                  <div class="coin-flip text-gray-400 dark:text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" width="300" height="300"> <defs> <!-- Gradientes basados en una paleta tecnológica/IA (púrpuras profundos, índigo y cian neón) --> <linearGradient id="outerBorder" x1="0%" y1="0%" x2="100%" y2="100%"> <stop offset="0%" stop-color="#1e1b4b"/> <!-- Indigo muy oscuro --> <stop offset="50%" stop-color="#312e81"/> <!-- Indigo medio --> <stop offset="100%" stop-color="#020617"/> <!-- Casi negro --> </linearGradient> <linearGradient id="innerBase" x1="0%" y1="100%" x2="100%" y2="0%"> <stop offset="0%" stop-color="#09090b"/> <stop offset="100%" stop-color="#17172b"/> <!-- Tono oscuro con leve matiz violeta --> </linearGradient> <linearGradient id="textGrad" x1="0%" y1="0%" x2="0%" y2="100%"> <stop offset="0%" stop-color="#ffffff"/> <stop offset="100%" stop-color="#22d3ee"/> <!-- Cian brillante --> </linearGradient> <linearGradient id="accentPrimary" x1="0%" y1="0%" x2="100%" y2="100%"> <stop offset="0%" stop-color="#c084fc"/> <!-- Púrpura claro/neón --> <stop offset="100%" stop-color="#2563eb"/> <!-- Azul rey --> </linearGradient> <filter id="glow" x="-20%" y="-20%" width="140%" height="140%"> <feGaussianBlur stdDeviation="10" result="blur" /> <feComposite in="SourceGraphic" in2="blur" operator="over" /> </filter> <filter id="textGlow" x="-10%" y="-10%" width="120%" height="120%"> <feGaussianBlur stdDeviation="4" result="blur" /> <feComposite in="SourceGraphic" in2="blur" operator="over" /> </filter> </defs> <!-- Resplandor exterior en tono violeta/azul --> <circle cx="200" cy="200" r="185" fill="#6366f1" filter="url(#glow)" opacity="0.25" /> <!-- Capas base de la moneda --> <circle cx="200" cy="200" r="180" fill="url(#outerBorder)" /> <circle cx="200" cy="200" r="165" fill="url(#innerBase)" /> <!-- Anillos internos decorativos --> <circle cx="200" cy="200" r="150" fill="none" stroke="#4c1d95" stroke-width="4" /> <circle cx="200" cy="200" r="135" fill="none" stroke="#22d3ee" stroke-width="2" stroke-dasharray="4 8" opacity="0.7" /> <!-- Acentos visuales estilo circuito integrados con la nueva paleta --> <path d="M 75 200 L 110 165 L 140 165" fill="none" stroke="url(#accentPrimary)" stroke-width="3" stroke-linejoin="round"/> <circle cx="75" cy="200" r="5" fill="url(#accentPrimary)"/> <circle cx="140" cy="165" r="4" fill="url(#accentPrimary)"/> <path d="M 325 200 L 290 235 L 260 235" fill="none" stroke="url(#accentPrimary)" stroke-width="3" stroke-linejoin="round"/> <circle cx="325" cy="200" r="5" fill="url(#accentPrimary)"/> <circle cx="260" cy="235" r="4" fill="url(#accentPrimary)"/> <!-- Nodos secundarios --> <path d="M 200 75 L 200 110 L 225 135" fill="none" stroke="#6366f1" stroke-width="2" stroke-linejoin="round" opacity="0.6"/> <circle cx="200" cy="75" r="4" fill="#6366f1"/> <circle cx="225" cy="135" r="3" fill="#6366f1"/> <path d="M 200 325 L 200 290 L 175 265" fill="none" stroke="#6366f1" stroke-width="2" stroke-linejoin="round" opacity="0.6"/> <circle cx="200" cy="325" r="4" fill="#6366f1"/> <circle cx="175" cy="265" r="3" fill="#6366f1"/> <!-- Símbolo M31 --> <text x="200" y="240" font-family="'Inter', 'Segoe UI', 'Roboto', sans-serif" font-weight="900" font-size="120" text-anchor="middle" fill="url(#textGrad)" letter-spacing="2" filter="url(#textGlow)">M31</text> <!-- Texto perimetral superior --> <path id="topCurve" d="M 80 200 A 120 120 0 0 1 320 200" fill="transparent" /> <text font-family="'Inter', 'Segoe UI', 'Roboto', sans-serif" font-size="13" font-weight="700" fill="#a5b4fc" letter-spacing="4"> <textPath href="#topCurve" startOffset="50%" text-anchor="middle">IONET TOKEN</textPath> </text> <!-- Texto perimetral inferior --> <path id="bottomCurve" d="M 80 200 A 120 120 0 0 0 320 200" fill="transparent" /> <text font-family="'Inter', 'Segoe UI', 'Roboto', sans-serif" font-size="13" font-weight="700" fill="#a5b4fc" letter-spacing="4"> <textPath href="#bottomCurve" startOffset="50%" text-anchor="middle">DIGITAL COIN</textPath> </text> </svg>
                  </div>
                  <div class="mt-4 text-sm text-gray-500 dark:text-gray-400">Calculando tokens…</div>
                </div>
              } @else if (usageDashboardError()) {
                <div class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                  {{ usageDashboardError() }}
                </div>
              } @else if (usageDashboard()) {
                <div class="grid grid-cols-2 gap-3 text-sm">
                  <div class="rounded-lg bg-gray-50 p-3 dark:bg-zinc-900">
                    <div class="text-[11px] text-gray-400 flex items-center gap-1" title="Tokens de entrada consumidos"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-emerald-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19V5m0 0-6 6m6-6 6 6" /></svg><span>Consumida</span><span class="sr-only">Entrada consumida</span></div>
                    <div class="font-semibold text-gray-900 dark:text-gray-100">{{ formatTokenCount(usageDashboard()!.InputTokens) }}</div>
                  </div>
                  <div class="rounded-lg bg-gray-50 p-3 dark:bg-zinc-900">
                    <div class="text-[11px] text-gray-400 flex items-center gap-1" title="Tokens de salida consumidos"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-sky-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m0 0 6-6m-6 6-6-6" /></svg><span>Consumida</span><span class="sr-only">Salida consumida</span></div>
                    <div class="font-semibold text-gray-900 dark:text-gray-100">{{ formatTokenCount(usageDashboard()!.OutputTokens) }}</div>
                  </div>
                  <div class="rounded-lg bg-emerald-50 p-3 dark:bg-emerald-950/20">
                    <div class="text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1" title="Tokens de entrada habilitados"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-emerald-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19V5m0 0-6 6m6-6 6 6" /></svg><span>Habilitada</span><span class="sr-only">Entrada habilitada</span></div>
                    <div class="font-semibold text-emerald-900 dark:text-emerald-100">{{ formatTokenCount(usageDashboard()!.InputTokensEnabled) }}</div>
                    <div class="mt-1 text-[10px] text-emerald-700/70 dark:text-emerald-300/70">{{ formatPercent(usageDashboard()!.InputTokensPercentUsed) }} consumido</div>
                  </div>
                  <div class="rounded-lg bg-emerald-50 p-3 dark:bg-emerald-950/20">
                    <div class="text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1" title="Tokens de salida habilitados"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-sky-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m0 0 6-6m-6 6-6-6" /></svg><span>Habilitada</span><span class="sr-only">Salida habilitada</span></div>
                    <div class="font-semibold text-emerald-900 dark:text-emerald-100">{{ formatTokenCount(usageDashboard()!.OutputTokensEnabled) }}</div>
                    <div class="mt-1 text-[10px] text-emerald-700/70 dark:text-emerald-300/70">{{ formatPercent(usageDashboard()!.OutputTokensPercentUsed) }} consumido</div>
                  </div>
                  <div class="rounded-lg bg-amber-50 p-3 dark:bg-amber-950/20">
                    <div class="text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1" title="Tokens de entrada restantes"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-emerald-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19V5m0 0-6 6m6-6 6 6" /></svg><span>Restante</span><span class="sr-only">Entrada restante</span></div>
                    <div class="font-semibold text-amber-900 dark:text-amber-100">{{ formatTokenCount(usageDashboard()!.InputTokensRemaining) }}</div>
                  </div>
                  <div class="rounded-lg bg-amber-50 p-3 dark:bg-amber-950/20">
                    <div class="text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1" title="Tokens de salida restantes"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block h-3 w-3 shrink-0 text-sky-500" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m0 0 6-6m-6 6-6-6" /></svg><span>Restante</span><span class="sr-only">Salida restante</span></div>
                    <div class="font-semibold text-amber-900 dark:text-amber-100">{{ formatTokenCount(usageDashboard()!.OutputTokensRemaining) }}</div>
                  </div>
                  <div class="rounded-lg bg-gray-50 p-3 dark:bg-zinc-900">
                    <div class="text-[11px] text-gray-400">Sesiones</div>
                    <div class="font-semibold text-gray-900 dark:text-gray-100">{{ usageDashboard()!.Sessions }}</div>
                  </div>
                  <div class="rounded-lg bg-gray-50 p-3 dark:bg-zinc-900">
                    <div class="text-[11px] text-gray-400">Mensajes</div>
                    <div class="font-semibold text-gray-900 dark:text-gray-100">{{ usageDashboard()!.Messages }}</div>
                  </div>
                  <div class="rounded-lg bg-gray-50 p-3 dark:bg-zinc-900">
                    <div class="text-[11px] text-gray-400">Usuarios activos</div>
                    <div class="font-semibold text-gray-900 dark:text-gray-100">{{ usageDashboard()!.ActiveUsers }}</div>
                  </div>
                  <div class="rounded-lg bg-gray-50 p-3 dark:bg-zinc-900">
                    <div class="text-[11px] text-gray-400">Tiempo promedio</div>
                    <div class="font-semibold text-gray-900 dark:text-gray-100">{{ formatDuration(usageDashboard()!.AverageDurationMs) }}</div>
                  </div>
                </div>
                <div class="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
                  Total: {{ formatTokenQuantity(usageDashboard()!.TotalTokens) }} · Razonamiento: {{ formatTokenCount(usageDashboard()!.ReasoningTokens) }} · Errores: {{ usageDashboard()!.FailedMessages }}
                </div>
              } @else {
                <div class="text-sm text-gray-500 dark:text-gray-400">No hay consumo registrado todavía.</div>
              }
            </div>
          </div>
        </div>
      }

      @if (microsoftSessionExpired()) {
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
          <span>
            Tu sesión de Microsoft caducó. Vuelve a conectarte para seguir usando este agente;
            no perderás la conversación ni tendrás que escribir tu contraseña de M31.
          </span>
          <button
            type="button"
            (click)="reconectarMicrosoft()"
            [disabled]="isReconnecting()"
            class="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
          >
            {{ isReconnecting() ? 'Conectando…' : 'Reconectar con Microsoft' }}
          </button>
        </div>
      }

      @if (tokenLimitNotice()) {
        <div
          class="border-b px-4 py-2 text-xs"
          [ngClass]="tokenLimitState()?.IsBlocked
            ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200'
            : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200'"
        >
          {{ tokenLimitNotice() }}
        </div>
      }

      <div #messagesViewport class="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/40 dark:bg-zinc-900/20 relative">
        @if (healthStatus() === 'checking') {
          <div class="absolute inset-0 flex flex-col items-center justify-center bg-white/90 dark:bg-zinc-950/90 z-50 backdrop-blur-sm transition-all duration-300">
            <div class="w-8 h-8 border-2 border-t-ionet-600 rounded-full animate-spin mb-2"></div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Verificando conexión segura...</p>
          </div>
        }

        <!-- Bienvenida del agente: se pinta aquí y no se envía al modelo, así que no gasta tokens. -->
        <div class="flex justify-start agent-welcome">
          <div class="max-w-[96%] w-fit rounded-2xl rounded-bl-md border border-gray-200 bg-white px-4 py-3 text-sm leading-relaxed text-gray-800 shadow-sm dark:border-white/10 dark:bg-zinc-900 dark:text-gray-100">
            <p class="font-medium text-gray-900 dark:text-white">{{ welcome().saludo }}</p>

            @if (welcome().puedo.length > 0) {
            <p class="mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Te puedo ayudar a</p>
            <ul class="mt-1.5 space-y-1">
              @for (capacidad of welcome().puedo; track capacidad) {
                <li class="flex gap-2">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mt-1 h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m5 12.5 4.5 4.5L19 7.5" />
                  </svg>
                  <span>{{ capacidad }}</span>
                </li>
              }
            </ul>
            }

            @if (welcome().nota) {
              <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">{{ welcome().nota }}</p>
            }

            @if (attachmentsEnabled) {
              <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
                También puedes adjuntar un PDF, una imagen o un CSV con el clip de abajo.
              </p>
            }

            @if (messages().length === 0 && welcome().ejemplos.length > 0) {
              <p class="mt-4 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Prueba con</p>
              <div class="mt-1.5 flex flex-wrap gap-2">
                @for (ejemplo of welcome().ejemplos; track ejemplo) {
                  <button
                    type="button"
                    (click)="usarEjemplo(ejemplo)"
                    [disabled]="isPromptDisabled()"
                    title="Escribir esta pregunta en el cuadro de mensaje"
                    class="welcome-card rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-left text-xs text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:border-emerald-700 dark:hover:bg-emerald-900/20"
                  >{{ ejemplo }}</button>
                }
              </div>
            }
          </div>
        </div>

        @for (msg of messages(); track msg.at) {
          <div class="flex" [class.justify-end]="msg.role === 'user'" [class.justify-start]="msg.role === 'assistant'">
            <div
              class="rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm"
              [ngClass]="msg.role === 'user'
                ? 'max-w-[85%] bg-emerald-600 text-white rounded-br-md'
                : 'max-w-[96%] w-fit bg-white text-gray-800 border border-gray-200 dark:bg-zinc-900 dark:text-gray-100 dark:border-white/10 rounded-bl-md'"
            >
              @if (msg.role === 'assistant') {
                <app-chat-rich-response
                  [summary]="msg.summary"
                  [fallbackContent]="msg.content"
                  [blocks]="msg.blocks || []"
                  [tables]="msg.tables || []"
                  [sources]="msg.sources || []"
                  accent="neutral"
                />
              } @else {
                @if (msg.content) {
                  <div class="whitespace-pre-wrap">{{ msg.content }}</div>
                }
                @if (msg.attachments?.length) {
                  <div class="flex flex-wrap gap-1.5" [class.mt-2]="msg.content">
                    @for (adjunto of msg.attachments; track adjunto.name + $index) {
                      <span class="inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-2 py-1 text-[11px]">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="h-3.5 w-3.5 shrink-0" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M18.75 10.5 12.4 16.85a3.25 3.25 0 0 1-4.6-4.6l6.7-6.7a2.17 2.17 0 1 1 3.06 3.06l-6.7 6.7a1.08 1.08 0 1 1-1.53-1.53l5.9-5.9" />
                        </svg>
                        <span class="max-w-[180px] truncate">{{ adjunto.name }}</span>
                        <span class="opacity-75">{{ formatAttachmentSize(adjunto.sizeBytes) }}</span>
                      </span>
                    }
                  </div>
                }
              }
              <div
                class="mt-2 text-[10px] leading-none"
                [ngClass]="msg.role === 'user'
                  ? 'text-emerald-100/85 text-right'
                  : 'text-gray-400 dark:text-gray-500'"
              >
                {{ formatMessageTime(msg.at) }}
                @if (msg.role === 'assistant' && msg.durationMs !== undefined) {
                  <span> · respondió en {{ formatDuration(msg.durationMs) }}</span>
                }
                @if (showUsageDebugMetrics() && msg.role === 'assistant' && msg.usage) {
                  <span> · {{ formatTokenQuantity(msg.usage.TotalTokens) }}</span>
                }
              </div>
            </div>
          </div>
        }

        @if (isSending()) {
          <div class="flex justify-start">
            <div class="max-w-[85%] rounded-2xl rounded-bl-md px-4 py-3 text-sm leading-relaxed shadow-sm bg-white text-gray-800 border border-gray-200 dark:bg-zinc-900 dark:text-gray-100 dark:border-white/10">
              <div class="flex items-center gap-1.5">
                <span class="h-2 w-2 rounded-full bg-emerald-500 animate-bounce" style="animation-delay: -0.3s;"></span>
                <span class="h-2 w-2 rounded-full bg-emerald-500 animate-bounce" style="animation-delay: -0.15s;"></span>
                <span class="h-2 w-2 rounded-full bg-emerald-500 animate-bounce"></span>
              </div>
            </div>
          </div>
        }
      </div>

      @if (healthStatus() === 'online') {
        <div class="border-t border-gray-100 dark:border-white/5 p-4">
          @if (attachmentsEnabled && (attachments().length > 0 || attachmentError())) {
            <div class="mb-2 flex flex-wrap items-center gap-2">
              @for (adjunto of attachments(); track adjunto.id) {
                <span class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 py-1 pl-2 pr-1 text-[11px] text-gray-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-300">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M18.75 10.5 12.4 16.85a3.25 3.25 0 0 1-4.6-4.6l6.7-6.7a2.17 2.17 0 1 1 3.06 3.06l-6.7 6.7a1.08 1.08 0 1 1-1.53-1.53l5.9-5.9" />
                  </svg>
                  <span class="max-w-[160px] truncate">{{ adjunto.name }}</span>
                  <span class="text-gray-400">{{ formatAttachmentSize(adjunto.sizeBytes) }}</span>
                  <button
                    type="button"
                    (click)="removeAttachment(adjunto.id)"
                    title="Quitar este archivo"
                    aria-label="Quitar este archivo"
                    class="ml-0.5 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6 6 18" />
                    </svg>
                  </button>
                </span>
              }

              @if (isUploadingAttachment()) {
                <span class="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400" role="status" aria-live="polite">
                  <span class="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-emerald-500 dark:border-white/20 dark:border-t-emerald-400"></span>
                  Preparando archivo…
                </span>
              }

              @if (attachmentError()) {
                <span class="text-[11px] text-red-600 dark:text-red-400">{{ attachmentError() }}</span>
              }
            </div>
          }

          <div class="flex items-end gap-3">
          @if (attachmentsEnabled) {
          <input
            #fileInput
            type="file"
            multiple
            accept="application/pdf,image/png,image/jpeg,image/gif,image/webp,text/plain,text/csv,.csv,.txt,.md"
            class="hidden"
            (change)="onFilesSelected($event)"
          />
          <button
            type="button"
            (click)="fileInput.click()"
            [disabled]="isPromptDisabled() || isUploadingAttachment()"
            title="Adjuntar un archivo (PDF, imagen, texto o CSV; hasta 10 MB)"
            aria-label="Adjuntar un archivo"
            class="h-[42px] w-[42px] shrink-0 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed dark:border-white/10 dark:text-gray-400 dark:hover:bg-white/5 transition-colors flex items-center justify-center"
          >
            @if (isUploadingAttachment()) {
              <span class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-emerald-500 dark:border-white/20 dark:border-t-emerald-400"></span>
            } @else {
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="h-5 w-5" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M18.75 10.5 12.4 16.85a3.25 3.25 0 0 1-4.6-4.6l6.7-6.7a2.17 2.17 0 1 1 3.06 3.06l-6.7 6.7a1.08 1.08 0 1 1-1.53-1.53l5.9-5.9" />
            </svg>
            }
          </button>
          }
          <textarea
            #promptInput
            [(ngModel)]="draft"
            (keydown.enter)="handleEnter($event)"
            [disabled]="isPromptDisabled() || isUploadingAttachment()"
            rows="2"
            class="flex-1 resize-none rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
            [placeholder]="promptPlaceholder()"
          ></textarea>
          <button
            (click)="send()"
            [disabled]="isSendDisabled()"
            class="h-[42px] px-4 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-700 transition-colors"
          >
            {{ isSending() ? 'Enviando...' : 'Enviar' }}
          </button>
          </div>
        </div>
      }
    </div>
  `
})
export class CustomFoundryChatComponent implements OnInit {
  readonly agentId = input.required<string>();
  readonly title = input.required<string>();

  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly msalService = inject(MsalService, { optional: true });

  readonly messages = signal<UiMessage[]>([]);
  readonly isSending = signal(false);
  readonly healthStatus = signal<'checking' | 'online' | 'offline'>('checking');
  readonly healthDetail = signal('');
  readonly foundryClientSettings = signal<FoundryClientSettings>(DEFAULT_FOUNDRY_CLIENT_SETTINGS);
  readonly usageDashboardVisible = signal(false);
  readonly usageDashboardLoading = signal(false);
  readonly usageDashboard = signal<UsageDashboardSummary | null>(null);
  readonly usageDashboardError = signal('');
  readonly tokenLimitNotice = signal('');
  readonly tokenLimitState = signal<FoundryTokenLimitResponse | null>(null);
  private readonly messagesViewport = viewChild<ElementRef<HTMLDivElement>>('messagesViewport');

  draft = '';

  /** Bienvenida del agente activo; los textos viven en config/agent-welcome.ts. */
  readonly welcome = computed(() =>
    AGENT_WELCOME[this.agentId().trim().toUpperCase()] ?? DEFAULT_AGENT_WELCOME
  );

  private readonly promptInput = viewChild<ElementRef<HTMLTextAreaElement>>('promptInput');

  /**
   * Escribe el ejemplo en el cuadro, pero no lo envía: el usuario decide si lo manda tal cual o
   * lo ajusta. Enviarlo solo gastaría tokens en una pregunta que quizá no quería hacer.
   */
  usarEjemplo(texto: string): void {
    this.draft = texto;
    const campo = this.promptInput()?.nativeElement;
    if (campo) {
      campo.value = texto;
      campo.focus();
      campo.setSelectionRange(texto.length, texto.length);
    }
  }

  /** Máximos del adjunto: el tope real no es el disco, es el contexto que se paga en cada turno. */
  private static readonly MaxAttachmentBytes = 10 * 1024 * 1024;
  private static readonly MaxAttachments = 3;

  /** Interruptor de la función, configurable en environment.ts sin tocar el backend. */
  readonly attachmentsEnabled = environment.features.attachments;

  /** La sesión de Microsoft caducó: el chat se bloquea y se ofrece reconectar. */
  readonly microsoftSessionExpired = this.authService.microsoftSessionExpired;
  readonly isReconnecting = signal(false);

  readonly attachments = signal<AttachmentChip[]>([]);
  readonly attachmentError = signal('');
  private attachmentErrorTimer: number | null = null;
  readonly isUploadingAttachment = signal(false);
  private conversationId: string | null = null;
  private clientSessionId = this.createClientSessionId();
  private turnNumber = 1; // Contador de turnos para controlar límite de historial
  private lastActivityUtc: string | null = null;
  private readonly maxHealthRetries = 3;
  private readonly healthRetryDelayMs = 2000;

  constructor() {
    effect(() => {
      this.messages();
      this.isSending();

      const viewport = this.messagesViewport()?.nativeElement;
      if (!viewport) {
        return;
      }

      queueMicrotask(() => {
        viewport.scrollTop = viewport.scrollHeight;
      });
    });
  }

  ngOnInit(): void {
    this.restoreState();
    void this.loadFoundryClientSettings();
    void this.initializeAgentSession();
  }

  isSendDisabled(): boolean {
    // Con un archivo adjunto se puede enviar sin escribir nada: el archivo ya es el mensaje.
    return this.isPromptDisabled()
      || this.isUploadingAttachment()
      || (!this.draft.trim() && this.attachments().length === 0);
  }

  /** Sube el archivo al backend, que decide si va a Foundry o se lee como texto. */
  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const archivos = Array.from(input.files ?? []);
    input.value = '';

    if (archivos.length === 0) {
      return;
    }

    this.attachmentError.set('');

    for (const archivo of archivos) {
      if (this.attachments().length >= CustomFoundryChatComponent.MaxAttachments) {
        this.showAttachmentError(`Máximo ${CustomFoundryChatComponent.MaxAttachments} archivos por mensaje.`);
        break;
      }

      if (archivo.size > CustomFoundryChatComponent.MaxAttachmentBytes) {
        this.showAttachmentError(`"${archivo.name}" pesa más de 10 MB.`);
        continue;
      }

      await this.uploadAttachment(archivo);
    }
  }

  /** Muestra el aviso y lo retira a los 3 segundos: informa, pero no se queda estorbando. */
  private showAttachmentError(mensaje: string): void {
    this.attachmentError.set(mensaje);

    if (this.attachmentErrorTimer !== null) {
      clearTimeout(this.attachmentErrorTimer);
    }

    this.attachmentErrorTimer = setTimeout(() => {
      this.attachmentError.set('');
      this.attachmentErrorTimer = null;
    }, 3000) as unknown as number;
  }

  private async uploadAttachment(archivo: File): Promise<void> {
    this.isUploadingAttachment.set(true);

    try {
      const token = await this.getValidMsalToken();
      const formulario = new FormData();
      formulario.append('file', archivo, archivo.name);

      const respuesta = await firstValueFrom(
        this.http.post<{ Id: string; Name: string; Kind: string; SizeBytes: number }>(
          `${environment.apiUrl}/foundry/attachments?agentId=${encodeURIComponent(this.agentId())}`,
          formulario,
          { headers: this.buildBackendHeaders(token) }
        )
      );

      this.attachments.update(actuales => [...actuales, {
        id: respuesta.Id ?? (respuesta as Record<string, any>)['id'],
        name: respuesta.Name ?? archivo.name,
        kind: respuesta.Kind ?? 'file',
        sizeBytes: archivo.size
      }]);
    } catch (error) {
      const mensaje = (error as { error?: { Message?: string } })?.error?.Message;
      this.showAttachmentError(mensaje || `No se pudo adjuntar "${archivo.name}".`);
    } finally {
      this.isUploadingAttachment.set(false);
    }
  }

  removeAttachment(id: string): void {
    this.attachments.update(actuales => actuales.filter(adjunto => adjunto.id !== id));
  }

  formatAttachmentSize(bytes: number): string {
    if (bytes < 1024) { return `${bytes} B`; }
    if (bytes < 1024 * 1024) { return `${Math.round(bytes / 1024)} KB`; }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  isPromptDisabled(): boolean {
    return this.isSending()
      || this.microsoftSessionExpired()
      || this.healthStatus() !== 'online'
      || Boolean(this.tokenLimitState()?.IsBlocked);
  }

  promptPlaceholder(): string {
    if (this.microsoftSessionExpired()) {
      return 'Reconecta con Microsoft para seguir escribiendo.';
    }

    if (this.isUploadingAttachment()) {
      return 'Preparando el archivo…';
    }

    if (this.tokenLimitState()?.IsBlocked) {
      return 'Límite de tokens alcanzado. Contacta a tu distribuidor para desbloquear el servicio.';
    }

    return 'Escribe tu mensaje...';
  }

  handleEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (!keyboardEvent.shiftKey) {
      event.preventDefault();
      void this.send();
    }
  }

  async send(): Promise<void> {
    const text = this.draft.trim();
    if ((!text && this.attachments().length === 0) || this.isSending()) {
      return;
    }

    if (this.healthStatus() !== 'online') {
      this.messages.update(current => [
        ...current,
        {
          role: 'assistant',
          content: 'El agente no está disponible en este momento. Intenta de nuevo más tarde.',
          at: new Date().toISOString()
        }
      ]);
      return;
    }

    this.resetExpiredConversationIfNeeded();
    await this.refreshTokenLimitState();
    if (this.tokenLimitState()?.IsBlocked) {
      return;
    }

    const adjuntosDelTurno = this.attachments();
    const userMessage: UiMessage = {
      role: 'user',
      content: text,
      at: new Date().toISOString(),
      attachments: adjuntosDelTurno.length > 0
        ? adjuntosDelTurno.map(adjunto => ({ name: adjunto.name, sizeBytes: adjunto.sizeBytes }))
        : undefined
    };

    this.messages.update(current => [...current, userMessage]);
    this.draft = '';
    // Las fichas se vacían al enviar: el adjunto pertenece a ese turno, no a la conversación.
    this.attachments.set([]);
    this.attachmentError.set('');
    this.isSending.set(true);
    this.persistState();
    const sendStartedAt = performance.now();

    try {
      let msalToken = await this.getValidMsalToken();
      if (!msalToken && this.authService.requiresMicrosoftLoginForCurrentUser()) {
        throw new Error('No hay token de Entra ID disponible para consumir /foundry/chat.');
      }

      let response: FoundryChatResponse;
      try {
        response = await this.requestFoundry(text, msalToken, adjuntosDelTurno);
      } catch (err) {
        const status = (err as { status?: number } | null)?.status;
        if (status !== 401) {
          throw err;
        }

        msalToken = await this.getValidMsalToken(true);
        response = await this.requestFoundry(text, msalToken, adjuntosDelTurno);
      }

      this.conversationId = response.ConversationId;
      this.lastActivityUtc = new Date().toISOString();
      this.applyTokenLimitNotice(response.TokenLimits);
      this.turnNumber++; // Incrementar turno después de cada respuesta exitosa
      const normalizedReply = this.normalizeLineBreakTags(response.Reply);
      const parsed = this.parseAssistantContent(normalizedReply, response.References ?? []);
      this.messages.update(current => [
        ...current,
        {
          role: 'assistant',
          content: normalizedReply,
          at: response.CreatedUtc || new Date().toISOString(),
          durationMs: Math.max(0, Math.round(performance.now() - sendStartedAt)),
          usage: response.Usage,
          summary: parsed.summary,
          tables: parsed.tables,
          blocks: parsed.blocks,
          sources: parsed.sources
        }
      ]);
      this.persistState();
    } catch (error) {
      this.applyTokenLimitNotice(this.readTokenLimitsFromError(error));
      const { mensaje } = this.describirError(error);
      this.messages.update(current => [
        ...current,
        {
          role: 'assistant',
          content: mensaje,
          at: new Date().toISOString(),
          durationMs: Math.max(0, Math.round(performance.now() - sendStartedAt))
        }
      ]);
      this.persistState();
    } finally {
      this.isSending.set(false);
    }
  }

  private applyTokenLimitNotice(tokenLimits: FoundryTokenLimitResponse | null | undefined): void {
    this.tokenLimitState.set(tokenLimits ?? null);
    this.tokenLimitNotice.set(tokenLimits?.Message?.trim() ?? '');
  }

  resetConversation(): void {
    this.conversationId = null;
    this.clientSessionId = this.createClientSessionId();
    this.turnNumber = 1; // Resetear contador de turnos
    this.lastActivityUtc = null;
    this.tokenLimitState.set(null);
    this.tokenLimitNotice.set('');
    this.messages.set([]);
    this.persistState();
    void this.checkAgentHealthWithRetry();
  }

  showUsageDebugMetrics(): boolean {
    return this.foundryClientSettings().ShowUsageDebugMetrics;
  }

  showUsageDashboardButton(): boolean {
    return this.foundryClientSettings().UsageDashboardEnabled;
  }

  exportConversation(): void {
    const messages = this.messages();
    if (messages.length === 0) {
      return;
    }

    const exportedAt = new Date().toISOString();
    const fileName = `${this.buildExportBaseFileName(exportedAt)}.md`;

    this.downloadTextFile(fileName, this.buildConversationMarkdown(messages, exportedAt));
  }

  async exportConversationPdf(): Promise<void> {
    const messages = this.messages();
    if (messages.length === 0) {
      return;
    }

    const exportedAt = new Date().toISOString();
    const [{ jsPDF }, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    const autoTable = autoTableModule.default;
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const fileName = `${this.buildExportBaseFileName(exportedAt)}.pdf`;
    const user = this.authService.currentUser();
    const totals = this.conversationUsageTotals();
    const margin = 42;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - (margin * 2);
    let y = margin;

    const ensureSpace = (height: number): void => {
      if (y + height <= pageHeight - margin) {
        return;
      }

      doc.addPage();
      y = margin;
    };

    const writeText = (text: string, options?: { fontSize?: number; bold?: boolean; color?: [number, number, number]; gap?: number }): void => {
      const fontSize = options?.fontSize ?? 10;
      const gap = options?.gap ?? 10;
      doc.setFont('helvetica', options?.bold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(...(options?.color ?? [31, 41, 55]));
      const lines = doc.splitTextToSize(text || '(sin contenido)', contentWidth);
      ensureSpace((lines.length * (fontSize + 4)) + gap);
      doc.text(lines, margin, y);
      y += (lines.length * (fontSize + 4)) + gap;
    };

    doc.setProperties({
      title: `${this.title()} - ${this.conversationId ?? this.clientSessionId}`,
      subject: 'Historial de conversación M31',
      author: user?.email ?? 'M31'
    });

    writeText(this.title(), { fontSize: 18, bold: true, color: [17, 24, 39], gap: 14 });
    [
      `Usuario: ${user?.email ?? 'No disponible'}`,
      `Agente: ${this.agentId()}`,
      `Conversation ID: ${this.conversationId ?? 'No disponible'}`,
      `Session ID: ${this.clientSessionId}`,
      `Exportado: ${this.formatExportDate(exportedAt)}`,
      `Mensajes: ${messages.length}`,
      `Tokens de entrada: ${this.formatTokenCount(totals.inputTokens)}`,
      `Tokens de salida: ${this.formatTokenCount(totals.outputTokens)}`,
      `Tokens totales: ${this.formatTokenCount(totals.totalTokens)}`
    ].forEach(line => writeText(line, { fontSize: 9, color: [75, 85, 99], gap: 4 }));
    y += 10;

    for (const message of messages) {
      const role = message.role === 'user' ? 'Usuario' : 'Agente';
      ensureSpace(70);
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 18;
      writeText(`${role} · ${this.formatExportDate(message.at)}`, {
        fontSize: 12,
        bold: true,
        color: message.role === 'user' ? [4, 120, 87] : [17, 24, 39],
        gap: 8
      });

      this.writePdfMessageContent(doc, message, writeText);
      y += 2;

      if (message.tables && message.tables.length > 0) {
        for (const table of message.tables) {
          ensureSpace(80);
          const headers = table.headers.length > 0 ? table.headers : ['Columna'];
          const rows = table.rows.length > 0 ? table.rows : [];
          const columnCount = Math.max(headers.length, ...rows.map(row => row.length), 1);
          autoTable(doc, {
            startY: y,
            head: [this.normalizeMarkdownTableRow(headers, columnCount)],
            body: rows.map(row => this.normalizeMarkdownTableRow(row, columnCount)),
            margin: { left: margin, right: margin },
            tableWidth: 'auto',
            styles: {
              font: 'helvetica',
              fontSize: 8,
              cellPadding: 4,
              overflow: 'linebreak',
              valign: 'top',
              textColor: [31, 41, 55],
              lineColor: [229, 231, 235],
              lineWidth: 0.5
            },
            headStyles: {
              fillColor: [236, 253, 245],
              textColor: [6, 95, 70],
              fontStyle: 'bold'
            },
            alternateRowStyles: {
              fillColor: [249, 250, 251]
            }
          });
          y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 16;
        }
      }

      if (message.durationMs !== undefined) {
        writeText(`Tiempo de respuesta: ${this.formatDuration(message.durationMs)}`, { fontSize: 8, color: [107, 114, 128], gap: 4 });
      }

      if (message.usage) {
        writeText(
          `Tokens: entrada ${this.formatTokenCount(message.usage.InputTokens)}, salida ${this.formatTokenCount(message.usage.OutputTokens)}, total ${this.formatTokenCount(message.usage.TotalTokens)}`,
          { fontSize: 8, color: [107, 114, 128], gap: 4 }
        );
      }

      if (message.sources && message.sources.length > 0) {
        writeText('Fuentes:', { fontSize: 9, bold: true, color: [75, 85, 99], gap: 4 });
        message.sources.forEach((source, index) => {
          const page = source.pageNumber ? `, pagina ${source.pageNumber}` : '';
          const location = source.location ? ` - ${source.location}` : '';
          writeText(`${index + 1}. ${source.title}${page}${location}`, { fontSize: 8, color: [107, 114, 128], gap: 3 });
        });
      }
    }

    doc.save(fileName);
  }

  async openUsageDashboard(): Promise<void> {
    this.usageDashboardVisible.set(true);

    // La consulta suele tardar milisegundos. Se deja que la moneda complete un giro entero para
    // que la espera se lea como una transición y no como un parpadeo.
    // Aquí se controla el indicador, no dentro de loadUsageDashboard: debe apagarse cuando
    // terminan las dos cosas, la consulta y el giro.
    this.usageDashboardLoading.set(true);
    const giroMinimo = new Promise<void>(resolve => setTimeout(resolve, 6000));

    try {
      await Promise.all([this.loadUsageDashboard(false), giroMinimo]);
    } finally {
      this.usageDashboardLoading.set(false);
    }
  }

  private async loadUsageDashboard(controlarIndicador = true): Promise<void> {
    if (controlarIndicador) {
      this.usageDashboardLoading.set(true);
    }
    this.usageDashboardError.set('');

    try {
      let msalToken = await this.getValidMsalToken();
      if (!msalToken && this.authService.requiresMicrosoftLoginForCurrentUser()) {
        throw new Error('No hay token de Entra ID disponible para consultar consumo.');
      }

      try {
        const dashboard = await this.requestUsageDashboard(msalToken);
        this.usageDashboard.set(dashboard);
        this.applyTokenLimitNotice(this.resolveTokenLimitsFromDashboard(dashboard));
      } catch (err) {
        const status = (err as { status?: number } | null)?.status;
        if (status !== 401) {
          throw err;
        }

        msalToken = await this.getValidMsalToken(true);
        const dashboard = await this.requestUsageDashboard(msalToken);
        this.usageDashboard.set(dashboard);
        this.applyTokenLimitNotice(this.resolveTokenLimitsFromDashboard(dashboard));
      }
    } catch (error) {
      this.usageDashboard.set(null);
      this.usageDashboardError.set(this.resolveErrorMessage(error));
    } finally {
      if (controlarIndicador) {
        this.usageDashboardLoading.set(false);
      }
    }
  }

  private async refreshTokenLimitState(): Promise<void> {
    if (!this.showUsageDashboardButton()) {
      return;
    }

    try {
      let msalToken = await this.getValidMsalToken();
      if (!msalToken && this.authService.requiresMicrosoftLoginForCurrentUser()) {
        return;
      }

      try {
        const dashboard = await this.requestUsageDashboard(msalToken);
        this.usageDashboard.set(dashboard);
        this.applyTokenLimitNotice(this.resolveTokenLimitsFromDashboard(dashboard));
      } catch (err) {
        const status = (err as { status?: number } | null)?.status;
        if (status !== 401) {
          return;
        }

        msalToken = await this.getValidMsalToken(true);
        const dashboard = await this.requestUsageDashboard(msalToken);
        this.usageDashboard.set(dashboard);
        this.applyTokenLimitNotice(this.resolveTokenLimitsFromDashboard(dashboard));
      }
    } catch {
      // No bloquear el chat por una falla temporal consultando el tablero.
    }
  }

  private buildConversationMarkdown(messages: UiMessage[], exportedAt: string): string {
    const user = this.authService.currentUser();
    const totals = this.conversationUsageTotals();
    const lines = [
      `# ${this.title()}`,
      '',
      `- Usuario: ${user?.email ?? 'No disponible'}`,
      `- Agente: ${this.agentId()}`,
      `- Conversation ID: ${this.conversationId ?? 'No disponible'}`,
      `- Session ID: ${this.clientSessionId}`,
      `- Exportado: ${this.formatExportDate(exportedAt)}`,
      `- Mensajes: ${messages.length}`,
      `- Tokens de entrada: ${this.formatTokenCount(totals.inputTokens)}`,
      `- Tokens de salida: ${this.formatTokenCount(totals.outputTokens)}`,
      `- Tokens totales: ${this.formatTokenCount(totals.totalTokens)}`,
      '',
      '---',
      ''
    ];

    for (const message of messages) {
      const role = message.role === 'user' ? 'Usuario' : 'Agente';
      lines.push(`## ${role} · ${this.formatExportDate(message.at)}`, '');
      lines.push((message.content || '').trim() || '(sin contenido)', '');

      if (message.tables && message.tables.length > 0) {
        message.tables.forEach((table, index) => {
          lines.push(
            message.tables!.length > 1 ? `Tabla ${index + 1}:` : 'Tabla:',
            '',
            ...this.serializeMarkdownTable(table),
            ''
          );
        });
      }

      if (message.role === 'assistant' && message.durationMs !== undefined) {
        lines.push(`Tiempo de respuesta: ${this.formatDuration(message.durationMs)}`, '');
      }

      if (message.usage) {
        lines.push(
          `Tokens: entrada ${this.formatTokenCount(message.usage.InputTokens)}, salida ${this.formatTokenCount(message.usage.OutputTokens)}, total ${this.formatTokenCount(message.usage.TotalTokens)}`,
          ''
        );
      }

      if (message.sources && message.sources.length > 0) {
        lines.push('Fuentes:', '');
        message.sources.forEach((source, index) => {
          const page = source.pageNumber ? `, pagina ${source.pageNumber}` : '';
          const location = source.location ? ` - ${source.location}` : '';
          lines.push(`${index + 1}. ${source.title}${page}${location}`);
        });
        lines.push('');
      }

      lines.push('---', '');
    }

    return `${lines.join('\n').trim()}\n`;
  }

  private writePdfMessageContent(
    doc: { setFont: (fontName: string, fontStyle?: string) => void },
    message: UiMessage,
    writeText: (text: string, options?: { fontSize?: number; bold?: boolean; color?: [number, number, number]; gap?: number }) => void
  ): void {
    if (message.role === 'assistant' && message.blocks && message.blocks.length > 0) {
      for (const block of message.blocks) {
        if (block.type === 'heading' && block.heading) {
          writeText(block.heading, { fontSize: block.level === 2 ? 12 : 10, bold: true, gap: 6 });
        } else if (block.type === 'list' && block.items && block.items.length > 0) {
          block.items.forEach(item => writeText(`- ${item}`, { fontSize: 9, gap: 4 }));
        } else if (block.paragraph) {
          writeText(block.paragraph, { fontSize: 10, gap: 8 });
        }
      }
      return;
    }

    doc.setFont('helvetica', 'normal');
    this.removeMarkdownTables(message.content)
      .split(/\n{2,}/)
      .map(paragraph => paragraph.trim())
      .filter(Boolean)
      .forEach(paragraph => writeText(paragraph, { fontSize: 10, gap: 8 }));
  }

  private removeMarkdownTables(value: string): string {
    return value
      .split(/\r?\n/)
      .filter(line => !this.isMarkdownTableRow(line) && !this.isMarkdownTableSeparator(line))
      .join('\n')
      .trim();
  }

  private serializeMarkdownTable(table: ChatRichTable): string[] {
    const headers = table.headers.length > 0 ? table.headers : ['Columna'];
    const rows = table.rows.length > 0 ? table.rows : [];
    const columnCount = Math.max(headers.length, ...rows.map(row => row.length), 1);
    const normalizedHeaders = this.normalizeMarkdownTableRow(headers, columnCount);
    const separator = Array.from({ length: columnCount }, () => '---');

    return [
      this.formatMarkdownTableRow(normalizedHeaders),
      this.formatMarkdownTableRow(separator),
      ...rows.map(row => this.formatMarkdownTableRow(this.normalizeMarkdownTableRow(row, columnCount)))
    ];
  }

  private normalizeMarkdownTableRow(row: string[], columnCount: number): string[] {
    return Array.from({ length: columnCount }, (_, index) => row[index] ?? '');
  }

  private formatMarkdownTableRow(row: string[]): string {
    return `| ${row.map(cell => this.escapeMarkdownTableCell(cell)).join(' | ')} |`;
  }

  private escapeMarkdownTableCell(value: string): string {
    return String(value)
      .replace(/\|/g, '\\|')
      .replace(/\r?\n/g, '<br>')
      .trim();
  }

  private downloadTextFile(fileName: string, content: string): void {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private sanitizeFileName(value: string): string {
    return value.replace(/[^a-zA-Z0-9._-]/g, '-');
  }

  private buildExportBaseFileName(exportedAt: string): string {
    const conversationSuffix = this.conversationId
      ? `-${this.conversationId.slice(-10)}`
      : '';

    return this.sanitizeFileName(
      `m31-${this.agentId().toLowerCase()}-${this.formatFileTimestamp(exportedAt)}${conversationSuffix}`
    );
  }

  private formatFileTimestamp(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const pad = (part: number) => String(part).padStart(2, '0');
    return [
      date.getFullYear(),
      pad(date.getMonth() + 1),
      pad(date.getDate())
    ].join('')
      + '-'
      + [
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds())
      ].join('');
  }

  private formatExportDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  formatMessageTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  formatDuration(durationMs: number): string {
    if (durationMs < 1000) {
      return `${durationMs} ms`;
    }

    const seconds = durationMs / 1000;
    if (seconds < 60) {
      return `${seconds.toFixed(seconds >= 10 ? 0 : 1)} s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes} min ${remainingSeconds} s`;
  }

  formatTokenCount(tokens: number): string {
    if (Math.abs(tokens) >= 1_000_000) {
      const millions = tokens / 1_000_000;
      return `${new Intl.NumberFormat('es-MX', {
        maximumFractionDigits: millions % 1 === 0 ? 0 : 2
      }).format(millions)} M`;
    }

    return new Intl.NumberFormat('es-MX').format(tokens);
  }

  formatTokenQuantity(tokens: number): string {
    return `${this.formatTokenCount(tokens)} ${tokens === 1 ? 'token' : 'tokens'}`;
  }

  formatPercent(value: number): string {
    if (value > 0 && value < 0.01) {
      return '<0.01%';
    }

    return `${new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: value > 0 && value < 1 ? 2 : 0,
      maximumFractionDigits: 4
    }).format(value)}%`;
  }

  conversationUsageTotals(): ConversationUsageTotals {
    return this.messages()
      .filter(message => message.role === 'assistant' && message.usage)
      .reduce(
        (accumulator, message) => {
          const usage = message.usage;
          if (!usage) {
            return accumulator;
          }

          accumulator.inputTokens += usage.InputTokens;
          accumulator.outputTokens += usage.OutputTokens;
          accumulator.reasoningTokens += usage.ReasoningTokens;
          accumulator.totalTokens += usage.TotalTokens;
          return accumulator;
        },
        {
          inputTokens: 0,
          outputTokens: 0,
          reasoningTokens: 0,
          totalTokens: 0
        }
      );
  }

  private async loadFoundryClientSettings(): Promise<void> {
    try {
      const settings = await firstValueFrom(
        this.http.get<FoundryClientSettings>(`${environment.apiUrl}/foundry/settings`)
      );

      this.foundryClientSettings.set({
        ShowUsageDebugMetrics: Boolean(settings.ShowUsageDebugMetrics),
        UsageDashboardEnabled: Boolean(settings.UsageDashboardEnabled),
        ConversationInactivityTimeoutMinutes: this.normalizeInactivityTimeout(settings.ConversationInactivityTimeoutMinutes)
      });
      await this.refreshTokenLimitState();
    } catch {
      this.foundryClientSettings.set(DEFAULT_FOUNDRY_CLIENT_SETTINGS);
    }
  }

  private async initializeAgentSession(): Promise<void> {
    try {
      await this.restoreLicenseSession();
    } catch (error) {
      this.healthStatus.set('offline');
      this.healthDetail.set(this.resolveErrorMessage(error));
      return;
    }

    await this.checkAgentHealthWithRetry();
  }

  private async restoreLicenseSession(): Promise<void> {
    const licenseSessionTicket = this.authService.licenseSessionTicket();
    if (!licenseSessionTicket) {
      throw new Error('No hay licencias SIIC disponibles. Cierra sesión e inicia nuevamente.');
    }

    let msalToken = await this.getValidMsalToken();
    if (!msalToken && this.authService.requiresMicrosoftLoginForCurrentUser()) {
      throw new Error('No hay token de Entra ID disponible para actualizar las licencias SIIC.');
    }

    try {
      await this.requestLicenseSession(licenseSessionTicket, msalToken);
    } catch (error) {
      const status = (error as { status?: number } | null)?.status;
      if (status !== 401) {
        throw error;
      }

      msalToken = await this.getValidMsalToken(true);
      await this.requestLicenseSession(licenseSessionTicket, msalToken);
    }
  }

  private async requestLicenseSession(
    licenseSessionTicket: string,
    bearerToken: string | null
  ): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(
        `${environment.apiUrl}/foundry/license-session`,
        { LicenseSessionTicket: licenseSessionTicket },
        { headers: this.buildBackendHeaders(bearerToken) }
      )
    );
  }

  private async checkAgentHealthWithRetry(): Promise<void> {
    this.healthStatus.set('checking');
    this.healthDetail.set('');

    for (let attempt = 1; attempt <= this.maxHealthRetries; attempt += 1) {
      const healthy = await this.checkAgentHealthOnce();
      if (healthy) {
        return;
      }

      if (attempt < this.maxHealthRetries) {
        this.healthStatus.set('checking');
        await this.delay(this.healthRetryDelayMs);
      }
    }
  }

  private async checkAgentHealthOnce(): Promise<boolean> {
    this.healthStatus.set('checking');
    this.healthDetail.set('');

    const checkStartedAt = Date.now();
    let nextStatus: 'online' | 'offline' = 'offline';
    let nextDetail = '';

    try {
      let msalToken = await this.getValidMsalToken();
      if (!msalToken && this.authService.requiresMicrosoftLoginForCurrentUser()) {
        throw new Error('No hay token de Entra ID disponible para consumir /foundry/health.');
      }

      let response: FoundryHealthResponse;
      try {
        response = await this.requestFoundryHealth(msalToken);
      } catch (err) {
        const status = (err as { status?: number } | null)?.status;
        if (status !== 401) {
          throw err;
        }

        msalToken = await this.getValidMsalToken(true);
        response = await this.requestFoundryHealth(msalToken);
      }

      if (response.Available) {
        nextStatus = 'online';
        nextDetail = '';
      } else {
        nextStatus = 'offline';
        nextDetail = response.Detail || 'No se pudo validar disponibilidad con Foundry.';
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        // Backward-compatible fallback: allow chat when backend does not expose /foundry/health yet.
        nextStatus = 'online';
        nextDetail = '';
      } else {
        nextStatus = 'offline';
        nextDetail = this.resolveErrorMessage(error);
      }
    }

    const minimumCheckingMs = 900;
    const elapsedMs = Date.now() - checkStartedAt;
    if (elapsedMs < minimumCheckingMs) {
      await this.delay(minimumCheckingMs - elapsedMs);
    }

    this.healthStatus.set(nextStatus);
    this.healthDetail.set(nextDetail);
    return nextStatus === 'online';
  }

  private async delay(ms: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  private async requestFoundryHealth(bearerToken: string | null): Promise<FoundryHealthResponse> {
    const options = { headers: this.buildBackendHeaders(bearerToken) };

    return await firstValueFrom(
      this.http.get<FoundryHealthResponse>(
        `${environment.apiUrl}/foundry/health?agentId=${encodeURIComponent(this.agentId())}`,
        options
      )
    );
  }

  private async requestUsageDashboard(bearerToken: string | null): Promise<UsageDashboardSummary> {
    const options = { headers: this.buildBackendHeaders(bearerToken) };

    const response = await firstValueFrom(
      this.http.get<UsageDashboardSummary>(
        `${environment.apiUrl}/foundry/usage-dashboard`,
        options
      )
    );

    return this.normalizeUsageDashboard(response);
  }

  private normalizeUsageDashboard(response: UsageDashboardSummary | Record<string, unknown>): UsageDashboardSummary {
    const read = <T>(pascalName: string, camelName: string, fallback: T): T => {
      const source = response as Record<string, unknown>;
      return (source[pascalName] ?? source[camelName] ?? fallback) as T;
    };

    const normalized = {
      Domain: read('Domain', 'domain', ''),
      Sessions: Number(read('Sessions', 'sessions', 0)) || 0,
      Messages: Number(read('Messages', 'messages', 0)) || 0,
      ActiveUsers: Number(read('ActiveUsers', 'activeUsers', read('LocalResponses', 'localResponses', 0))) || 0,
      FailedMessages: Number(read('FailedMessages', 'failedMessages', 0)) || 0,
      InputTokens: Number(read('InputTokens', 'inputTokens', 0)) || 0,
      OutputTokens: Number(read('OutputTokens', 'outputTokens', 0)) || 0,
      InputTokensEnabled: Number(read('InputTokensEnabled', 'inputTokensEnabled', 0)) || 0,
      OutputTokensEnabled: Number(read('OutputTokensEnabled', 'outputTokensEnabled', 0)) || 0,
      InputTokensRemaining: Number(read('InputTokensRemaining', 'inputTokensRemaining', 0)) || 0,
      OutputTokensRemaining: Number(read('OutputTokensRemaining', 'outputTokensRemaining', 0)) || 0,
      InputTokensPercentUsed: 0,
      OutputTokensPercentUsed: 0,
      ReasoningTokens: Number(read('ReasoningTokens', 'reasoningTokens', 0)) || 0,
      TotalTokens: Number(read('TotalTokens', 'totalTokens', 0)) || 0,
      AverageDurationMs: Number(read('AverageDurationMs', 'averageDurationMs', 0)) || 0,
      UpdatedUtc: read('UpdatedUtc', 'updatedUtc', '')
    };

    if (normalized.InputTokensEnabled <= 0 && normalized.OutputTokensEnabled <= 0) {
      const fallbackEnabledTokens = this.resolveEnabledTokensFromCurrentLicenses();
      normalized.InputTokensEnabled = fallbackEnabledTokens.inputTokens;
      normalized.OutputTokensEnabled = fallbackEnabledTokens.outputTokens;
    }

    normalized.InputTokensRemaining = this.calculateRemainingTokens(normalized.InputTokens, normalized.InputTokensEnabled);
    normalized.OutputTokensRemaining = this.calculateRemainingTokens(normalized.OutputTokens, normalized.OutputTokensEnabled);
    normalized.InputTokensPercentUsed = this.calculateTokenPercent(normalized.InputTokens, normalized.InputTokensEnabled);
    normalized.OutputTokensPercentUsed = this.calculateTokenPercent(normalized.OutputTokens, normalized.OutputTokensEnabled);

    return normalized;
  }

  private readTokenLimitsFromError(error: unknown): FoundryTokenLimitResponse | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const payload = error.error as { TokenLimits?: FoundryTokenLimitResponse; tokenLimits?: FoundryTokenLimitResponse } | null;
    return payload?.TokenLimits ?? payload?.tokenLimits ?? null;
  }

  private resolveTokenLimitsFromDashboard(dashboard: UsageDashboardSummary): FoundryTokenLimitResponse | null {
    if (dashboard.InputTokensEnabled <= 0 && dashboard.OutputTokensEnabled <= 0) {
      return null;
    }

    const isInputBlocked = dashboard.InputTokensEnabled > 0 && dashboard.InputTokensRemaining <= 0;
    const isOutputBlocked = dashboard.OutputTokensEnabled > 0 && dashboard.OutputTokensRemaining <= 0;
    const isBlocked = isInputBlocked || isOutputBlocked;
    const shouldWarn = !isBlocked
      && ((dashboard.InputTokensEnabled > 0 && dashboard.InputTokensPercentUsed >= 30)
        || (dashboard.OutputTokensEnabled > 0 && dashboard.OutputTokensPercentUsed >= 30));

    if (!isBlocked && !shouldWarn) {
      return null;
    }

    return {
      InputTokensEnabled: dashboard.InputTokensEnabled,
      OutputTokensEnabled: dashboard.OutputTokensEnabled,
      InputTokensUsed: dashboard.InputTokens,
      OutputTokensUsed: dashboard.OutputTokens,
      InputTokensRemaining: dashboard.InputTokensRemaining,
      OutputTokensRemaining: dashboard.OutputTokensRemaining,
      InputTokensPercentUsed: dashboard.InputTokensPercentUsed,
      OutputTokensPercentUsed: dashboard.OutputTokensPercentUsed,
      ShouldWarn: shouldWarn,
      IsBlocked: isBlocked,
      Message: isBlocked
        ? 'Estimado cliente, ha llegado al límite de tokens disponibles de entrada o salida, favor de contactar con su distribuidor para desbloquear el servicio.'
        : `El consumo de la organización alcanzó el 30% o más del límite asignado. Entrada: ${this.formatPercent(dashboard.InputTokensPercentUsed)}, salida: ${this.formatPercent(dashboard.OutputTokensPercentUsed)}.`
    };
  }

  private async requestFoundry(
    message: string,
    bearerToken: string | null,
    adjuntos: AttachmentChip[]
  ): Promise<FoundryChatResponse> {
    const options = { headers: this.buildBackendHeaders(bearerToken) };

    return await firstValueFrom(
      this.http.post<FoundryChatResponse>(
        `${environment.apiUrl}/foundry/chat`,
        {
          agentId: this.agentId(),
          message,
          conversationId: this.conversationId,
          sessionId: this.clientSessionId,
          turnNumber: this.turnNumber, // Enviar número de turno al backend
          attachments: adjuntos.map(adjunto => ({ Id: adjunto.id, Name: adjunto.name, Kind: adjunto.kind }))
        },
        options
      )
    );
  }

  private buildBackendHeaders(bearerToken: string | null): HttpHeaders {
    let headers = new HttpHeaders();
    if (bearerToken) {
      headers = headers.set('Authorization', `Bearer ${bearerToken}`);
    }

    const siicUserEmail = this.authService.currentUser()?.email?.trim();
    if (siicUserEmail) {
      headers = headers.set('X-M31-User-Email', siicUserEmail);
    }

    // Credencial de quienes entraron solo con SIIC, sin login de Microsoft. Si además hay token
    // de Entra ID, el backend usa ése y el ticket queda como respaldo.
    const licenseSessionTicket = this.authService.licenseSessionTicket()?.trim();
    if (licenseSessionTicket) {
      headers = headers.set('X-M31-License-Ticket', licenseSessionTicket);
    }

    return headers;
  }

  private resolveEnabledTokensFromCurrentLicenses(): { inputTokens: number; outputTokens: number } {
    return this.authService.licencias().reduce((totals, license) => {
      const deployment = this.parseLicenseDeployment(license.ModeloDespliegue);
      return {
        inputTokens: totals.inputTokens + deployment.inputTokens,
        outputTokens: totals.outputTokens + deployment.outputTokens
      };
    }, { inputTokens: 0, outputTokens: 0 });
  }

  private parseLicenseDeployment(value: string | null | undefined): { inputTokens: number; outputTokens: number } {
    const parts = (value ?? '')
      .split('-')
      .map(part => part.trim())
      .filter(Boolean);

    return {
      inputTokens: this.parseTokenLimit(parts[2]),
      outputTokens: this.parseTokenLimit(parts[3])
    };
  }

  private parseTokenLimit(value: string | undefined): number {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      return 0;
    }

    const multiplier = /m$/i.test(trimmed) ? 1_000_000 : 1;
    const numericText = trimmed.replace(/m$/i, '').replace(',', '.').trim();
    const numericValue = Number(numericText);
    return Number.isFinite(numericValue) ? Math.round(numericValue * multiplier) : 0;
  }

  private calculateTokenPercent(consumedTokens: number, enabledTokens: number): number {
    if (!Number.isFinite(consumedTokens) || !Number.isFinite(enabledTokens) || enabledTokens <= 0) {
      return 0;
    }

    return Math.min(100, Math.round((consumedTokens / enabledTokens) * 1_000_000) / 10_000);
  }

  private calculateRemainingTokens(consumedTokens: number, enabledTokens: number): number {
    if (!Number.isFinite(consumedTokens) || !Number.isFinite(enabledTokens) || enabledTokens <= 0) {
      return 0;
    }

    return Math.max(0, enabledTokens - consumedTokens);
  }

  private storageKey(): string {
    const email = this.authService.currentUser()?.email?.toLowerCase() ?? 'anonimo';
    return `m31_foundry_${email}_${this.agentId().toUpperCase()}`;
  }

  private persistState(): void {
    sessionStorage.setItem(
      this.storageKey(),
      JSON.stringify({
        conversationId: this.conversationId,
        sessionId: this.clientSessionId,
        turnNumber: this.turnNumber, // Guardar turno actual
        lastActivityUtc: this.lastActivityUtc,
        messages: this.messages()
      })
    );
  }

  private restoreState(): void {
    const raw = sessionStorage.getItem(this.storageKey());
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as { conversationId?: string; sessionId?: string; turnNumber?: number; lastActivityUtc?: string; messages?: UiMessage[] };
      this.conversationId = parsed.conversationId ?? null;
      this.clientSessionId = parsed.sessionId || this.createClientSessionId();
      this.turnNumber = parsed.turnNumber ?? 1; // Restaurar turno actual
      this.lastActivityUtc = parsed.lastActivityUtc ?? null;
      const restoredMessages = Array.isArray(parsed.messages)
        ? parsed.messages.map(message => {
            if (message.role !== 'assistant') {
              return message;
            }

            const parsedContent = this.parseAssistantContent(message.content);
            return {
              ...message,
              summary: message.summary ?? parsedContent.summary,
              tables: message.tables ?? parsedContent.tables,
              blocks: message.blocks ?? parsedContent.blocks,
              sources: message.sources ?? parsedContent.sources
            };
          })
        : [];

      this.messages.set(restoredMessages);
    } catch {
      this.conversationId = null;
      this.clientSessionId = this.createClientSessionId();
      this.lastActivityUtc = null;
      this.messages.set([]);
    }
  }

  private resetExpiredConversationIfNeeded(): void {
    if (!this.conversationId || !this.lastActivityUtc) {
      return;
    }

    const lastActivityTime = new Date(this.lastActivityUtc).getTime();
    if (Number.isNaN(lastActivityTime)) {
      this.resetConversationContext();
      return;
    }

    const timeoutMs = this.foundryClientSettings().ConversationInactivityTimeoutMinutes * 60 * 1000;
    if (Date.now() - lastActivityTime < timeoutMs) {
      return;
    }

    this.resetConversationContext();
  }

  private resetConversationContext(): void {
    this.conversationId = null;
    this.clientSessionId = this.createClientSessionId();
    this.turnNumber = 1;
    this.lastActivityUtc = null;
    this.messages.set([]);
    this.persistState();
  }

  private normalizeInactivityTimeout(value: number): number {
    if (!Number.isFinite(value) || value < 1) {
      return DEFAULT_FOUNDRY_CLIENT_SETTINGS.ConversationInactivityTimeoutMinutes;
    }

    return Math.floor(value);
  }

  private createClientSessionId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }

    return `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  private parseAssistantContent(reply: string, embeddedReferences: ReferenceFoundryChatResponse[] = []): {
    summary: string | undefined;
    tables: ChatRichTable[];
    blocks: ChatRichBlock[];
    sources: ChatRichSource[];
  } {
    const normalizedReply = this.normalizeLineBreakTags(reply);
    if (!normalizedReply) {
      return { summary: undefined, tables: [], blocks: [], sources: [] };
    }

    const sourceStart = this.findSourceSectionStart(normalizedReply);
    const summaryRaw = sourceStart >= 0 ? normalizedReply.slice(0, sourceStart).trim() : normalizedReply.trim();
    const sourceSection = sourceStart >= 0 ? normalizedReply.slice(sourceStart) : '';

    const parsedTables = this.extractTablesFromText(summaryRaw);
    const cleanedSummary = this.cleanupSummaryText(parsedTables.cleanedText);
    const summary = cleanedSummary.length > 0 ? cleanedSummary : undefined;
    const blocks = this.buildSummaryBlocks(cleanedSummary);

    const embeddedSources = embeddedReferences
      .map(reference => (
        {
          title: reference.FileName?.trim() ?? '',
          location: reference.SasUrl?.trim() ?? '',
          pageNumber: reference.PageNumber > 0 ? reference.PageNumber : undefined,
          excerpt: `Archivo ubicado en ${reference.Folder?.trim() ?? 'ubicación desconocida'} con tipo de contenido ${reference.ContentType ?? 'desconocido'}.`,
          imageUrl: reference.ImageUrl?.trim() || undefined,
          imageBase64: reference.ImageBase64?.trim() || undefined
         }
      ))
      .filter(source => source.title.length > 0);
    const parsedSources = this.extractSources(sourceSection);
    const inlineLinkSources = this.extractInlineLinkSources(normalizedReply);
    const sources = this.mergeSources(embeddedSources, inlineLinkSources, parsedSources);

    return { summary, tables: parsedTables.tables, blocks, sources };
  }

  private normalizeLineBreakTags(text: string): string {
    return (text ?? '').replace(/<br\s*\/?\s*>/gi, '\n');
  }

  private mergeSources(...sourceGroups: ChatRichSource[][]): ChatRichSource[] {
    const merged: ChatRichSource[] = [];
    const seen = new Set<string>();

    for (const group of sourceGroups) {
      for (const source of group) {
        const key = `${source.title}|${source.location ?? ''}|${source.excerpt ?? ''}`.toLowerCase();
        if (seen.has(key)) {
          continue;
        }

        seen.add(key);
        merged.push(source);
      }
    }

    return merged;
  }

  private findSourceSectionStart(reply: string): number {
    const patterns = [
      /(?:^|\n)\s*Fuente:/i,
      /(?:^|\n)\s*Fuentes:/i,
      /(?:^|\n)\s*#{1,6}\s*Referencias?\s*:?/i,
      /(?:^|\n)\s*Referencias?\s*:?/i,
      /(?:^|\n)\s*[\uD83D\uDCC4]?\s*Fuente de informaci[oó]n\s*:?/i
    ];

    let start = -1;
    for (const pattern of patterns) {
      const match = reply.match(pattern);
      if (!match || match.index === undefined) {
        continue;
      }

      if (start === -1 || match.index < start) {
        start = match.index;
      }
    }

    return start;
  }

  private extractSources(sourceSection: string): ChatRichSource[] {
    if (!sourceSection.trim()) {
      return [];
    }

    const sources: ChatRichSource[] = [];
    const seen = new Set<string>();
    const addSource = (source: ChatRichSource) => {
      const key = `${source.title}|${source.location ?? ''}|${source.excerpt ?? ''}`.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        sources.push(source);
      }
    };

    const lines = sourceSection.split('\n').map(line => line.trim()).filter(Boolean);

    for (const line of lines) {
      const normalized = this.stripInlineMarkdown(line);
      if (!normalized) {
        continue;
      }

      const fuenteMatch = normalized.match(/^fuentes?:\s*(.+)$/i);
      if (fuenteMatch?.[1]) {
        addSource({ title: fuenteMatch[1].trim() });
        continue;
      }

      const bulletSourceMatch = normalized.match(/^[-*]\s*(.+?)\s*:\s*(.+)$/);
      if (bulletSourceMatch?.[1] && bulletSourceMatch?.[2]) {
        addSource({
          title: bulletSourceMatch[1].trim(),
          excerpt: bulletSourceMatch[2].trim()
        });
        continue;
      }

      const infoMatch = normalized.match(/^fuente de informaci[oó]n:\s*(.+)$/i);
      if (infoMatch?.[1]) {
        addSource({ title: infoMatch[1].trim() });
        continue;
      }

      const locationMatch = normalized.match(/^ubicaci[oó]n:\s*(.+)$/i);
      if (locationMatch?.[1]) {
        if (sources.length > 0) {
          sources[sources.length - 1] = {
            ...sources[sources.length - 1],
            location: locationMatch[1].trim()
          };
        } else {
          addSource({ title: 'Referencia', location: locationMatch[1].trim() });
        }
      }
    }

    return sources;
  }

  private extractInlineLinkSources(reply: string): ChatRichSource[] {
    const sources: ChatRichSource[] = [];
    const seen = new Set<string>();
    const addSource = (title: string, location: string) => {
      const normalizedLocation = this.normalizeExternalUrl(location);
      if (!normalizedLocation || seen.has(normalizedLocation)) {
        return;
      }

      seen.add(normalizedLocation);
      sources.push({
        title: title.trim() || this.fileNameFromUrl(normalizedLocation),
        location: normalizedLocation
      });
    };

    const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi;
    for (const match of reply.matchAll(markdownLinkPattern)) {
      addSource(match[1], match[2]);
    }

    const urlPattern = /https?:\/\/[^\s<>"')\]}]+/gi;
    for (const match of reply.matchAll(urlPattern)) {
      addSource('', match[0]);
    }

    return sources;
  }

  private isSafeExternalUrl(value: string): boolean {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
      return false;
    }
  }

  private normalizeExternalUrl(value: string): string | undefined {
    const normalized = (value || '')
      .trim()
      .replace(/`/g, '')
      .replace(/[.,;:!?]+$/g, '');

    return this.isSafeExternalUrl(normalized) ? normalized : undefined;
  }

  private fileNameFromUrl(value: string): string {
    try {
      const url = new URL(value);
      const path = url.pathname.replace(/\/$/, '');
      const fileName = path.slice(path.lastIndexOf('/') + 1);
      return decodeURIComponent(fileName || url.hostname);
    } catch {
      return 'Documento';
    }
  }

  private cleanupSummaryText(text: string): string {
    return text
      .replace(/^\s*Respuesta:\s*/i, '')
      .replace(/^\s*Tabla comparativa:\s*$/gim, '')
      .replace(/^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/gim, '')
      .replace(/^\s*>\s?/gim, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\[([^\]]*)\]\([^\)]*\)/g, (_match, label: string) => label.replace(/`/g, ''))
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private buildSummaryBlocks(text: string): ChatRichBlock[] {
    if (!text.trim()) {
      return [];
    }

    const lines = text.split('\n');
    const blocks: ChatRichBlock[] = [];
    let paragraphLines: string[] = [];
    let listItems: string[] = [];

    const flushParagraph = () => {
      if (paragraphLines.length === 0) {
        return;
      }

      blocks.push({
        type: 'paragraph',
        paragraph: paragraphLines.join('\n').trim()
      });
      paragraphLines = [];
    };

    const flushList = () => {
      if (listItems.length === 0) {
        return;
      }

      blocks.push({
        type: 'list',
        items: [...listItems]
      });
      listItems = [];
    };

    for (const rawLine of lines) {
      const trimmedLine = rawLine.trim();
      if (this.isMarkdownDivider(trimmedLine)) {
        flushParagraph();
        flushList();
        continue;
      }

      const headingMatch = trimmedLine.match(/^\s{0,3}(#{1,6})\s+(.+)$/);
      if (headingMatch?.[1] && headingMatch?.[2]) {
        flushParagraph();
        flushList();
        blocks.push({
          type: 'heading',
          level: headingMatch[1].length,
          heading: this.stripInlineMarkdown(headingMatch[2])
        });
        continue;
      }

      const emphasizedHeading = trimmedLine.match(/^\*\*(.+?)\*\*:?\s*$/);
      if (emphasizedHeading?.[1] && this.looksLikeStandaloneHeading(emphasizedHeading[1])) {
        flushParagraph();
        flushList();
        blocks.push({
          type: 'heading',
          level: 3,
          heading: this.stripInlineMarkdown(emphasizedHeading[1])
        });
        continue;
      }

      const line = this.stripInlineMarkdown(trimmedLine);
      if (!line) {
        flushParagraph();
        flushList();
        continue;
      }

      if (/^(?:[-*•]\s+|\d+[\.)]\s+)/.test(line)) {
        flushParagraph();
        listItems.push(line.replace(/^(?:[-*•]\s+|\d+[\.)]\s+)/, '').trim());
      } else {
        flushList();
        paragraphLines.push(line);
      }
    }

    flushParagraph();
    flushList();
    return blocks;
  }

  private isMarkdownDivider(text: string): boolean {
    return /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/.test(text);
  }

  private looksLikeStandaloneHeading(text: string): boolean {
    const normalized = text.trim().replace(/:$/, '');
    if (!normalized || /[.!?]$/.test(normalized)) {
      return false;
    }

    const wordCount = normalized.split(/\s+/).filter(Boolean).length;
    return wordCount <= 9;
  }

  private extractTablesFromText(text: string): { cleanedText: string; tables: ChatRichTable[] } {
    const lines = text.split('\n');
    const tables: ChatRichTable[] = [];
    const keptLines: string[] = [];

    let index = 0;
    while (index < lines.length) {
      const current = lines[index]?.trim() ?? '';
      const next = lines[index + 1]?.trim() ?? '';

      const looksLikeHeader = this.isMarkdownTableRow(current);
      const looksLikeSeparator = this.isMarkdownTableSeparator(next);

      if (looksLikeHeader && looksLikeSeparator) {
        const headerCells = this.parseMarkdownTableRow(current);
        const rows: string[][] = [];
        index += 2;

        while (index < lines.length && this.isMarkdownTableRow(lines[index]?.trim() ?? '')) {
          const rowCells = this.parseMarkdownTableRow(lines[index].trim());
          if (rowCells.length > 0) {
            rows.push(rowCells);
          }
          index += 1;
        }

        if (headerCells.length > 0 && rows.length > 0) {
          const normalizedRows = rows.map(row => {
            const cells = row.slice(0, headerCells.length);
            while (cells.length < headerCells.length) {
              cells.push('');
            }
            return cells;
          });

          tables.push({ headers: headerCells, rows: normalizedRows });
          continue;
        }
      }

      keptLines.push(lines[index]);
      index += 1;
    }

    const cleanedText = keptLines
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return { cleanedText, tables };
  }

  private isMarkdownTableRow(line: string): boolean {
    if (!line.startsWith('|')) {
      return false;
    }

    const pipeCount = (line.match(/\|/g) ?? []).length;
    return pipeCount >= 2;
  }

  private isMarkdownTableSeparator(line: string): boolean {
    return /^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line);
  }

  private parseMarkdownTableRow(line: string): string[] {
    return line
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(cell => this.stripInlineMarkdown(cell.trim()));
  }

  private stripInlineMarkdown(text: string): string {
    return text
      .replace(/^\s{0,3}#{1,6}\s+/, '')
      .replace(/^\s*>\s?/, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\[(.*?)\]\([^\)]*\)/g, '$1')
      .trim();
  }

  private async getValidMsalToken(forceRefresh = false): Promise<string | null> {
    if (!this.msalService) {
      return this.authService.msalAccessToken();
    }

    const activeAccount = this.msalService.instance.getActiveAccount();
    const fallbackAccount = this.msalService.instance.getAllAccounts()[0] ?? null;
    const account = activeAccount ?? fallbackAccount;

    if (!account) {
      return this.authService.msalAccessToken();
    }

    try {
      const scopes = getMsalScopesForActiveClientId();
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes,
        account,
        forceRefresh,
        redirectUri: getSilentRedirectUri()
      });

      if (result?.accessToken) {
        this.authService.setMicrosoftToken(result.accessToken);
        return result.accessToken;
      }
    } catch {
      // No se pudo renovar en silencio. Suele pasar al volver a la plataforma uno o dos días
      // después: la sesión de Microsoft ya no se puede refrescar sin que el usuario participe.
    }

    // Antes se devolvía aquí el token guardado como último recurso. Si ya había caducado, la
    // aplicación lo seguía dando por bueno: el chat quedaba vivo pero cada mensaje fallaba con
    // 401 y el agente aparecía como "No disponible", sin forma de reconectar salvo limpiar la
    // caché del navegador. Ahora un token caducado se trata como lo que es: no hay token.
    const guardado = this.authService.msalAccessToken();
    const caducidad = AuthService.fechaCaducidadToken(guardado);
    if (guardado && caducidad !== null && caducidad <= Date.now()) {
      this.authService.marcarSesionMicrosoftCaducada();
      return null;
    }

    return guardado;
  }

  /**
   * Devuelve al usuario al inicio de sesión de Microsoft conservando su sesión del SIIC: no
   * tiene que volver a escribir su contraseña, y al regresar sigue en la misma conversación.
   */
  async reconectarMicrosoft(): Promise<void> {
    if (!this.msalService) {
      return;
    }

    this.isReconnecting.set(true);

    try {
      // Lo que estaba escribiendo se guarda: el redirect recarga la página entera.
      this.persistState();

      const cuenta = this.msalService.instance.getActiveAccount()
        ?? this.msalService.instance.getAllAccounts()[0]
        ?? null;

      await this.msalService.instance.loginRedirect({
        scopes: getMsalScopesForActiveClientId(),
        loginHint: cuenta?.username ?? this.authService.currentUser()?.email ?? undefined
      });
    } catch (error) {
      console.error('[MSAL] No se pudo reconectar con Microsoft.', error);
      this.isReconnecting.set(false);
    }
  }

  /**
   * Convierte cualquier fallo en algo que el usuario final pueda entender y accionar, y deja el
   * detalle técnico en la consola del navegador.
   *
   * El usuario nunca ve el error de Azure ni el JSON: ve una frase en su idioma y un código.
   * El código lleva la categoría y la hora, que es lo que necesitamos para encontrar la línea
   * correspondiente en el registro del backend (que ya guarda usuario, agente y momento).
   */
  private describirError(error: unknown): { mensaje: string; codigo: string } {
    const estado = error instanceof HttpErrorResponse ? error.status : 0;
    const crudo = this.textoCrudoDelError(error).toLowerCase();

    let categoria = 'GEN';
    let mensaje = 'No pudimos completar tu solicitud. Vuelve a intentarlo en unos momentos.';

    if (estado === 0 && error instanceof HttpErrorResponse) {
      categoria = 'RED';
      mensaje = 'No hay conexión con el servidor. Revisa tu conexión a internet e inténtalo de nuevo.';
    } else if (estado === 401 || estado === 403 || crudo.includes('no hay token')) {
      categoria = 'AUT';
      mensaje = 'Tu sesión caducó. Vuelve a conectarte con Microsoft para continuar.';
      this.authService.marcarSesionMicrosoftCaducada();
    } else if (crudo.includes('valid license') || crudo.includes('licencia')) {
      categoria = 'LIC';
      mensaje = 'Tu cuenta no tiene habilitada la licencia necesaria para consultar documentos. '
        + 'Comparte este código con soporte para que la revisen.';
    } else if (crudo.includes('sharepoint') || crudo.includes('tool_server_error') || estado === 424) {
      categoria = 'DOC';
      mensaje = 'El servicio de documentos no respondió. Vuelve a enviar tu pregunta; suele funcionar al segundo intento.';
    } else if (estado === 429) {
      categoria = 'SAT';
      mensaje = 'El agente está atendiendo muchas solicitudes en este momento. Espera unos segundos e inténtalo de nuevo.';
    } else if (estado === 408 || crudo.includes('timeout') || crudo.includes('tiempo de espera')) {
      categoria = 'TMP';
      mensaje = 'El agente tardó demasiado en responder. Vuelve a intentarlo.';
    } else if (estado >= 500) {
      categoria = 'AGE';
      mensaje = 'El agente no está disponible en este momento. Inténtalo de nuevo en unos minutos.';
    }

    const codigo = this.generarCodigoError(categoria);

    // El detalle técnico vive aquí, no en la pantalla del usuario.
    console.error(`[M31][${codigo}] Fallo en el chat del agente ${this.agentId()}:`, error);

    return { mensaje: `${mensaje} (código ${codigo})`, codigo };
  }

  /** Código corto y rastreable: categoría + día y hora, que es como se busca en el backend. */
  private generarCodigoError(categoria: string): string {
    const ahora = new Date();
    const dos = (valor: number) => String(valor).padStart(2, '0');
    const sello = `${dos(ahora.getMonth() + 1)}${dos(ahora.getDate())}-${dos(ahora.getHours())}${dos(ahora.getMinutes())}`;
    const azar = Math.random().toString(36).slice(2, 4).toUpperCase();
    return `M31-${categoria}-${sello}-${azar}`;
  }

  private textoCrudoDelError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      try {
        return `${error.message} ${typeof error.error === 'string' ? error.error : JSON.stringify(error.error)}`;
      } catch {
        return error.message ?? '';
      }
    }

    return error instanceof Error ? error.message : String(error ?? '');
  }

  private resolveErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const err = error.error as { detail?: string; title?: string; Message?: string; Reply?: string; reply?: string } | string | null;

      if (typeof err === 'string' && err.trim()) {
        return err;
      }

      if (err && typeof err === 'object') {
        if (typeof err.detail === 'string' && err.detail.trim()) {
          return err.detail;
        }

        if (typeof err.Message === 'string' && err.Message.trim()) {
          return err.Message;
        }

        if (typeof err.Reply === 'string' && err.Reply.trim()) {
          return err.Reply;
        }

        if (typeof err.reply === 'string' && err.reply.trim()) {
          return err.reply;
        }

        if (typeof err.title === 'string' && err.title.trim()) {
          return err.title;
        }
      }

      return error.message || 'No se pudo completar la solicitud a Foundry.';
    }

    if (error instanceof Error && error.message.trim()) {
      return error.message;
    }

    return 'No se pudo completar la solicitud a Foundry.';
  }
}
